import {useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {useReactToPrint} from "react-to-print";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import MapPopover from "@/components/ui/map-popover";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {cn} from "@/lib/utils";
import {BadgeOverflowList, DetailField, Loader, ModalCreatedFooter, VehicleDetailField} from "@/src/modules/core/components";
import {getCreatorDisplayName} from "@/src/modules/core/lib/get-creator-display-name";
import {useStoredMe} from "@/src/modules/core/store/me";

import {useCitation} from "../hooks/use-citation";
import {CITATION_PRINT_PAGE_STYLE} from "../lib/citation-print-page-style";
import {
  tCitationAllTypesTitle,
  tCitationPlateExpiresNotRecorded,
  tCitationTowBadgeLabel,
} from "../lib/citation-list-i18n";
import {formatPlateExpiresOn} from "../lib/plate-expires-on";
import {parseAppLocale} from "@/src/modules/core/i18n";
import {tCitationTypeLabel} from "../lib/citation-type-i18n";
import {getTowWorkflowStatus} from "../lib/tow-workflow";
import {
  formatCitationTowCompanyLine,
  getCitationTowDisplay,
} from "../lib/get-citation-tow-display";
import {
  getCitationProofSectionTitle,
  getCitationTowDispatchFiles,
  getCitationTowProofSectionTitle,
} from "../lib/get-citation-tow-dispatch-files";
import {CitationDetailPrintView} from "./citation-detail-print-view";
import {CitationImageSection} from "./citation-image-section";
import {CitationStoredVehicleOriginSection} from "./citation-stored-vehicle-origin-section";
import type {CitationFile, CitationTypeEmbedded} from "../types";

type Props = {
  open: boolean;
  citationId: number | null;
  onOpenChange: (open: boolean) => void;
};

export function CitationDetailModal({open, citationId, onOpenChange}: Props) {
  const {t: tCommon} = useTranslation("common");
  const {t: tCitation, i18n} = useTranslation("citation");
  const appLocale = parseAppLocale(i18n.language);
  const me = useStoredMe();
  const printRef = useRef<HTMLDivElement>(null);
  const {citation, isPending} = useCitation(open && citationId ? citationId : null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: citation ? `Citation #${citation.citation_id}` : "Citation",
    pageStyle: CITATION_PRINT_PAGE_STYLE,
  });

  const typeItems = useMemo((): {ct: CitationTypeEmbedded; label: string}[] => {
    if (!citation) return [];
    return (citation.citation_types ?? []).flatMap((ct) => {
      const label = tCitationTypeLabel(ct, tCitation);
      return label ? [{ct, label}] : [];
    });
  }, [citation, tCitation]);
  const attachmentFiles = citation?.files ?? [];
  const proofFiles = citation?.citation_proof?.files ?? [];
  const towFiles = citation ? getCitationTowDispatchFiles(citation) : [];

  const imageSections = useMemo(() => {
    if (!citation) return [];

    const sections: {key: string; title: string; files: CitationFile[]}[] = [];

    if (attachmentFiles.length > 0) {
      sections.push({
        key: "attachments",
        title: tCitation("detail.attachments", {defaultValue: "Attachments"}),
        files: attachmentFiles,
      });
    }
    if (proofFiles.length > 0) {
      sections.push({
        key: "proof",
        title: getCitationProofSectionTitle(citation),
        files: proofFiles,
      });
    }

    return sections;
  }, [attachmentFiles, citation, proofFiles, tCitation]);

  const showLocation =
    Boolean(citation?.gps_coordinates?.trim()) && (me?.isAdmin || me?.isSeniorSupervisor);
  const reportedBy = getCreatorDisplayName(
    citation?.issued_by
      ? {
          id: citation.issued_by.user_id,
          name: citation.issued_by.name,
          user_name: citation.issued_by.user_name ?? undefined,
        }
      : null,
  );
  const detailsText = citation?.details?.trim() || null;
  const towDisplay = citation ? getCitationTowDisplay(citation) : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] gap-0 overflow-x-hidden overflow-y-auto p-4 sm:max-w-[700px] sm:p-6">
          {isPending || !citation ? (
            <DialogHeader>
              <DialogTitle className="sr-only">{tCommon("loading")}</DialogTitle>
              <Loader className="h-[10rem]" />
            </DialogHeader>
          ) : (
            <div className="flex min-w-0 flex-col gap-4">
              <DialogHeader className="gap-1 p-0 text-left">
                <DialogTitle className="pr-10 text-base">
                  {tCitation("detail.title", {
                    defaultValue: "Citation #{{id}}",
                    id: citation.citation_id,
                  })}
                </DialogTitle>
              </DialogHeader>

              <div className="grid min-w-0 gap-x-4 gap-y-3 sm:grid-cols-2">
                <VehicleDetailField vehicle={citation.vehicle} />
                <DetailField label={tCommon("reported_by")}>{reportedBy}</DetailField>
                <DetailField
                  label={tCitation("detail.plate_expires_on", {
                    defaultValue: "Plate / registration expires",
                  })}
                >
                  {citation.plate_expires_on ? (
                    <span className="tabular-nums">
                      {formatPlateExpiresOn(citation.plate_expires_on, appLocale)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      {tCitationPlateExpiresNotRecorded(tCitation)}
                    </span>
                  )}
                </DetailField>
                {showLocation ? (
                  <DetailField label={tCitation("detail.location", {defaultValue: "Location"})}>
                    <MapPopover position={citation.gps_coordinates!} forceInteractive />
                  </DetailField>
                ) : null}
                {citation.household?.name?.trim() ? (
                  <DetailField label={tCitation("detail.household", {defaultValue: "Household"})}>
                    {citation.household.name}
                  </DetailField>
                ) : null}
                {typeItems.length > 0 ? (
                  <DetailField label={tCommon("type")} className="min-w-0 sm:col-span-2">
                    <BadgeOverflowList
                      items={typeItems}
                      maxVisible={typeItems.length}
                      getKey={(item) => String(item.ct.citation_type_id)}
                      getLabel={(item) => item.label}
                      visibleBadgeVariant="secondary"
                      visibleBadgeClassName="inline-flex h-auto min-h-7 w-full max-w-full min-w-0 items-start justify-start whitespace-normal py-1.5 text-left text-xs font-normal leading-snug [word-break:break-word]"
                      popoverItemClassName="max-w-none shrink-0 whitespace-normal py-1 text-left text-xs font-normal leading-snug"
                      popoverTitle={tCitationAllTypesTitle(tCitation)}
                      align="start"
                      clampVisibleLabel={false}
                      overflowPairLayout="flex"
                      className="w-full min-w-0"
                    />
                  </DetailField>
                ) : null}
                {detailsText ? (
                  <DetailField label={tCommon("details")} className="sm:col-span-2">
                    <p className="whitespace-pre-wrap break-words">{detailsText}</p>
                  </DetailField>
                ) : null}
                {citation.private_notes?.trim() && !me?.isResident ? (
                  <DetailField label={tCitation("detail.notes", {defaultValue: "Notes"})} className="sm:col-span-2">
                    <p className="whitespace-pre-wrap break-words">{citation.private_notes.trim()}</p>
                  </DetailField>
                ) : null}
              </div>

              {imageSections.length > 0 ? (
                <div
                  className={cn(
                    "grid min-w-0 grid-cols-1 items-stretch gap-4",
                    imageSections.length >= 2 && "sm:grid-cols-2",
                  )}
                >
                  {imageSections.map((section) => (
                    <CitationImageSection
                      key={section.key}
                      title={section.title}
                      files={section.files}
                    />
                  ))}
                </div>
              ) : null}

              <CitationStoredVehicleOriginSection citation={citation} />

              {towDisplay?.show ? (
                <div className="border-border flex flex-col gap-3 border-t pt-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium">
                      {tCitation("tow.label", {defaultValue: "Tow"})}
                    </p>
                    {towDisplay.badge.showWorkflow ? (
                      <Badge variant={towDisplay.badge.variant} className="text-xs font-medium">
                        {tCitationTowBadgeLabel(
                          tCitation,
                          getTowWorkflowStatus(citation),
                          towDisplay.badge.label,
                        )}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
                    {formatCitationTowCompanyLine(towDisplay.company, towDisplay.telephone) ? (
                      <DetailField label={tCitation("detail.tow_company", {defaultValue: "Tow company"})}>
                        {towDisplay.company && towDisplay.telephone ? (
                          <>
                            {towDisplay.company}{" "}
                            (
                            <a
                              href={`tel:${towDisplay.telephone.replace(/\s/g, "")}`}
                              className="text-primary hover:underline"
                            >
                              {towDisplay.telephone}
                            </a>
                            )
                          </>
                        ) : towDisplay.telephone ? (
                          <a
                            href={`tel:${towDisplay.telephone.replace(/\s/g, "")}`}
                            className="text-primary hover:underline"
                          >
                            {towDisplay.telephone}
                          </a>
                        ) : (
                          towDisplay.company
                        )}
                      </DetailField>
                    ) : null}
                    {towDisplay.dispatchDetails ? (
                      <DetailField
                        label={tCitation("detail.tow_dispatch_notes", {defaultValue: "Tow dispatch notes"})}
                        className="sm:col-span-2"
                      >
                        <p className="whitespace-pre-wrap break-words">{towDisplay.dispatchDetails}</p>
                      </DetailField>
                    ) : null}
                    {towDisplay.cancellationReason ? (
                      <DetailField
                        label={tCitation("detail.tow_cancel_reason", {defaultValue: "Tow cancel reason"})}
                        className="sm:col-span-2"
                      >
                        <p className="whitespace-pre-wrap break-words">
                          {towDisplay.cancellationReason}
                        </p>
                      </DetailField>
                    ) : null}
                  </div>
                  {towFiles.length > 0 ? (
                    <CitationImageSection
                      title={getCitationTowProofSectionTitle(citation)}
                      files={towFiles}
                    />
                  ) : null}
                </div>
              ) : null}

              <ModalCreatedFooter createdAt={citation.created_at}>
                <Button type="button" className="print:hidden" onClick={handlePrint}>
                  {tCommon("print")}
                </Button>
              </ModalCreatedFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {citation ? (
        <div
          aria-hidden
          className="pointer-events-none fixed top-0 -left-[10000px] z-[-1] w-[680px] overflow-hidden"
        >
          <div ref={printRef}>
            <CitationDetailPrintView citation={citation} now={new Date()} />
          </div>
        </div>
      ) : null}
    </>
  );
}
